import type { Requester } from '@labeling/globals';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, LANGUAGE_KEY, locales } from '@/constants/locales';
import { roleLabel } from './role-utils';
import { useRoleAssumption } from './use-role-assumption';

// ─── constants ────────────────────────────────────────────────────────────────

const DOCS_URL = 'https://baseplate.legogroup.io/docs/default/component/labeling-platform';

/**
 * This panel is a disclosure widget, not an ARIA menu: it holds labelled rows
 * with nested sub-lists and has no arrow-key navigation. Declaring role="menu"
 * /role="menuitem" would promise a keyboard contract we don't implement and
 * would also strip the native button/link roles from its children. So the
 * trigger just owns the panel via aria-expanded + aria-controls.
 */
const PANEL_ID = 'navbar-user-menu-panel';

// ─── tiny shared primitives ───────────────────────────────────────────────────

/** Chevron-down icon — used inside every value pill. */
const Chevron = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="shrink-0 text-core-slate-500"
  >
    <polyline points="2,3 5,7 8,3" />
  </svg>
);

/** External-link arrow — used on the Documentation row. */
const ExternalArrow = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
    className="shrink-0 text-core-slate-400"
  >
    <path d="M2 10 L10 2M5 2h5v5" />
  </svg>
);

/** Pill that shows the current value + chevron. Clicking it opens a sub-list. */
const ValuePill = ({
  value,
  onClick,
  ariaLabel,
}: {
  value: string;
  onClick: () => void;
  ariaLabel: string;
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="flex items-center gap-[4px] px-[8px] py-[3px] rounded-[6px] bg-core-slate-50 border border-core-slate-200 hover:bg-core-slate-100 transition-colors text-body-sm text-core-slate-1300 font-medium whitespace-nowrap"
  >
    {value}
    <Chevron />
  </button>
);

/** A row of options that appears below the pill when it is open. */
const OptionList = <T extends string>({
  options,
  selected,
  onSelect,
}: {
  options: { value: T; label: string }[];
  selected: T;
  onSelect: (v: T) => void;
}) => (
  <div className="mx-400 mb-[6px] rounded-[8px] border border-core-slate-200 overflow-hidden absolute right-[-18px]">
    {options.map((opt, i) => (
      <button
        key={opt.value}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(opt.value);
        }}
        className={[
          'w-full text-left px-[12px] py-[7px] text-body-sm transition-colors',
          selected === opt.value
            ? 'bg-core-blue-50 text-core-blue-700 font-medium'
            : 'bg-core-white text-core-slate-1300 hover:bg-core-slate-50',
          i > 0 ? 'border-t border-core-slate-100' : '',
        ].join(' ')}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// ─── main component ───────────────────────────────────────────────────────────

/**
 * Hamburger → panel:
 *
 *   Language          [EN ▾]
 *     ↳ EN / PL  (inline option list when open)
 *   Documentation        ↗
 *   ────────────────────── (only when multi-role)
 *   Role           [Admin ▾]
 *     ↳ Admin / User / …
 *   ──────────────────────
 *   Sign Out
 */
export const UserMenuDropdown = ({ onSignOut }: { onSignOut: () => void }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { hasMultipleRoles, sortedRoles, currentRoleType, selectRole } = useRoleAssumption();

  // ── locale ──
  const [locale, setLocale] = useState<string>(() => {
    const stored = localStorage.getItem(LANGUAGE_KEY) ?? DEFAULT_LOCALE;
    return locales.includes(stored) ? stored : DEFAULT_LOCALE;
  });

  const handleLocaleSelect = (lng: string) => {
    setLocale(lng);
    localStorage.setItem(LANGUAGE_KEY, lng);
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  // ── role ──
  const handleRoleSelect = (type: Requester['type']) => {
    selectRole(type);
    setRoleOpen(false);
  };

  // ── close panel on Escape ──
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // ── close panel on outside click ──
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  // Close sub-lists when panel closes
  useEffect(() => {
    if (!isOpen) {
      setLangOpen(false);
      setRoleOpen(false);
    }
  }, [isOpen]);

  return (
    <div className="relative">
      {/* ── hamburger trigger ── */}
      <button
        ref={buttonRef}
        type="button"
        aria-label={t('navbar.display.userMenu')}
        aria-expanded={isOpen}
        aria-controls={PANEL_ID}
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center justify-center w-[36px] h-[36px] text-core-white rounded-100 hover:bg-core-white/10 transition-colors duration-200"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <line x1="1" y1="4" x2="17" y2="4" />
          <line x1="1" y1="9" x2="17" y2="9" />
          <line x1="1" y1="14" x2="17" y2="14" />
        </svg>
      </button>

      {/* ── panel ── */}
      {isOpen && (
        <div
          ref={menuRef}
          id={PANEL_ID}
          className="absolute right-0 top-full mt-[6px] z-[200] w-[210px] bg-core-white border border-core-slate-200 rounded-[12px] shadow-lg overflow-hidden py-[6px]"
        >
          {/* ── Language row ── */}
          <div className="flex items-center justify-between px-[16px] h-[40px]">
            <span className="text-body-md text-core-slate-1300">
              {t('navbar.display.language')}
            </span>
            <ValuePill
              value={locale.toUpperCase()}
              ariaLabel={t('navbar.display.language')}
              onClick={() => {
                setLangOpen((v) => !v);
                setRoleOpen(false);
              }}
            />
          </div>
          {langOpen && (
            <OptionList
              options={locales.map((l) => ({ value: l, label: l.toUpperCase() }))}
              selected={locale}
              onSelect={handleLocaleSelect}
            />
          )}

          {/* ── Documentation row ── */}
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-between px-[16px] h-[40px] hover:bg-core-slate-50 transition-colors"
          >
            <span className="text-body-md text-core-slate-1300">
              {t('navbar.display.documentation')}
            </span>
            <ExternalArrow />
          </a>

          {/* ── Role row (multi-role only) ── */}
          {hasMultipleRoles && (
            <>
              <div className="border-t border-core-slate-200 mx-[16px] my-[4px]" />
              <div className="flex items-center justify-between px-[16px] h-[40px]">
                <span className="text-body-md text-core-slate-1300">
                  {t('navbar.display.role')}
                </span>
                <ValuePill
                  value={roleLabel(currentRoleType, t)}
                  ariaLabel={t('navbar.display.role')}
                  onClick={() => {
                    setRoleOpen((v) => !v);
                    setLangOpen(false);
                  }}
                />
              </div>
              {roleOpen && (
                <OptionList
                  options={sortedRoles.map((r) => ({
                    value: r.type,
                    label: roleLabel(r.type, t),
                  }))}
                  selected={currentRoleType}
                  onSelect={handleRoleSelect}
                />
              )}
            </>
          )}

          {/* ── Sign out row ── */}
          <div className="border-t border-core-slate-200 mx-[16px] my-[4px]" />
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              onSignOut();
            }}
            className="flex items-center w-full text-left px-[16px] h-[40px] text-body-md text-core-red-700 hover:bg-core-slate-50 transition-colors"
          >
            {t('navbar.display.signOut')}
          </button>
        </div>
      )}
    </div>
  );
};
